/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useState, useRef, useMemo} from 'react';
import {ChatMessage as ChatMessageType} from '../../types';
import MarkdownRenderer from './MarkdownRenderer';
import {latexToSpeechText} from '../../utils/mathUtils';
import '../../styles/components/ChatMessage.css';

interface ChatMessageProps {
  message: ChatMessageType;
  onSpeak?: (text: string, messageId: string, rate: number, action: 'play' | 'pause' | 'stop') => void;
  isSpeaking?: boolean;
  isPaused?: boolean;
  onEdit?: (messageId: string, newContent: string) => void;
  onTryAgain?: (messageId: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onShare?: (messageId: string) => void;
  onExportPDF?: (messageId: string) => void;
  editCount?: number;
  regenerateCount?: number;
  currentVersion?: number;
  totalVersions?: number;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onSpeak,
  isSpeaking = false,
  isPaused = false,
  onEdit,
  onTryAgain,
  onLike,
  onDislike,
  onShare,
  onExportPDF,
  editCount = 0,
  regenerateCount = 0,
  currentVersion = 1,
  totalVersions = 1,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [showTTSPopup, setShowTTSPopup] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // ===== FIXED: Memoize the speech text conversion =====
  const speechText = useMemo(() => {
    if (!message.content) return 'No readable content';
    
    let text = latexToSpeechText(message.content);
    text = text
      .replace(/^#{1,6}\s*([^\n]*)$/gm, '$1. ')
      .replace(/#{1,6}\s*/g, '')
      .replace(/\$/g, '')
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/___(.+?)___/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/_(.+?)_/g, '$1')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^\d+\.\s+/gm, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, 'image: $1')
      .replace(/\|[-:|\s]+\|/g, '')
      .replace(/-{2,}/g, '')
      .replace(/(?<!\d)-{1}(?!\d)/g, ' ')
      .replace(/\+/g, ' plus ')
      .replace(/=/g, ' equals ')
      .replace(/</g, ' less than ')
      .replace(/>/g, ' greater than ')
      .replace(/\s+([.,!?])/g, '$1')
      .replace(/([.,!?])\s+/g, '$1 ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/([.!?])\s*([.!?])/g, '$1 ')
      .replace(/([.!?])\s*$/g, '$1');
    
    return text || 'No readable content';
  }, [message.content]);

  // ===== FIXED: Memoize words array =====
  const words = useMemo(() => {
    return speechText.split(/(\s+|[.,!?])/).filter((word) => word.trim().length > 0);
  }, [speechText]);

  // Convert content to natural speech text (kept for backward compatibility)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const convertToSpeechText = (text: string): string => {
    return speechText;
  };

  // Split text into words for highlighting (kept for backward compatibility)
  const getWords = (text: string): string[] => {
    return words;
  };

  // Handle TTS play, pause, and stop
  const handleSpeak = (action: 'play' | 'pause' | 'stop' = 'play') => {
    if (action === 'stop' || (isSpeaking && action === 'play' && !isPaused)) {
      window.speechSynthesis.cancel();
      setHighlightedWordIndex(-1);
      setShowTTSPopup(false);
      utteranceRef.current = null;
      if (onSpeak) onSpeak('', message.id || '', speechRate, 'stop');
    } else if (action === 'pause' && isSpeaking && !isPaused) {
      window.speechSynthesis.pause();
      if (onSpeak) onSpeak('', message.id || '', speechRate, 'pause');
    } else if ((action === 'play' && !isSpeaking) || (action === 'play' && isPaused)) {
      if (onSpeak) {
        onSpeak(speechText, message.id || '', speechRate, 'play');
        setShowTTSPopup(true);

        const utterance = new SpeechSynthesisUtterance(speechText);
        utterance.rate = speechRate;
        utteranceRef.current = utterance;

        let currentWordIndex = 0;

        utterance.onboundary = (event) => {
          if (event.name === 'word') {
            setHighlightedWordIndex(currentWordIndex);
            currentWordIndex++;
            if (currentWordIndex >= words.length) {
              currentWordIndex = 0;
            }
          }
        };

        utterance.onend = () => {
          setHighlightedWordIndex(-1);
          setShowTTSPopup(false);
          utteranceRef.current = null;
          if (onSpeak) onSpeak('', message.id || '', speechRate, 'stop');
        };

        window.speechSynthesis.speak(utterance);
      }
    }
  };

  // Handle speech rate change
  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRate = parseFloat(e.target.value);
    setSpeechRate(newRate);
    if (isSpeaking && onSpeak) {
      window.speechSynthesis.cancel();
      onSpeak(speechText, message.id || '', newRate, 'play');
      setShowTTSPopup(true);

      const utterance = new SpeechSynthesisUtterance(speechText);
      utterance.rate = newRate;
      utteranceRef.current = utterance;

      let currentWordIndex = 0;

      utterance.onboundary = (event) => {
        if (event.name === 'word') {
          setHighlightedWordIndex(currentWordIndex);
          currentWordIndex++;
          if (currentWordIndex >= words.length) {
            currentWordIndex = 0;
          }
        }
      };

      utterance.onend = () => {
        setHighlightedWordIndex(-1);
        setShowTTSPopup(false);
        utteranceRef.current = null;
        if (onSpeak) onSpeak('', message.id || '', speechRate, 'stop');
      };

      window.speechSynthesis.speak(utterance);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (onEdit && editContent.trim()) {
      onEdit(message.id || '', editContent);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    if (isDisliked) setIsDisliked(false);
    if (onLike) onLike(message.id || '');
  };

  const handleDislike = () => {
    setIsDisliked(!isDisliked);
    if (isLiked) setIsLiked(false);
    if (onDislike) onDislike(message.id || '');
  };

  const handleTryAgain = () => {
    if (onTryAgain) onTryAgain(message.id || '');
  };

  const handleShare = async () => {
    if (onShare) {
      onShare(message.id || '');
    } else {
      try {
        const shareUrl = `${window.location.origin}/message/${message.id}`;
        await navigator.clipboard.writeText(shareUrl);
        console.log('Share link copied to clipboard');
      } catch (err) {
        console.error('Failed to copy share link:', err);
      }
    }
  };

  const handleExportPDF = () => {
    if (onExportPDF) {
      onExportPDF(message.id || '');
    }
  };

  // ===== FIXED: Memoize the highlighted content =====
  const renderedContent = useMemo(() => {
    if (!isSpeaking || highlightedWordIndex < 0) {
      return <MarkdownRenderer content={message.content} />;
    }

    // Split the content into words for highlighting
    const contentWords = message.content.split(/(\s+|[.,!?])/).filter(word => word.trim().length > 0);
    
    return (
      <div ref={contentRef}>
        {contentWords.map((word, index) => (
          <span
            key={`${index}-${word}`}
            className={index === highlightedWordIndex ? 'tts-highlight' : ''}
            style={{display: 'inline'}}
          >
            {word}
          </span>
        ))}
      </div>
    );
  }, [message.content, isSpeaking, highlightedWordIndex]); // Proper dependencies

  return (
    <div className={`chat-message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}>
      <div className="message-avatar">
        <span className="avatar-icon">{message.role === 'user' ? '👤' : '🤖'}</span>
      </div>
      <div className="message-content-wrapper">
        <div className="message-bubble">
          {isEditing ? (
            <div className="edit-container">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="edit-textarea"
                rows={4}
              />
              <div className="edit-buttons">
                <button onClick={handleSaveEdit} className="save-btn">✓</button>
                <button onClick={handleCancelEdit} className="cancel-btn">✕</button>
              </div>
            </div>
          ) : (
            renderedContent
          )}
          {showTTSPopup && (
            <div className="tts-popup">
              <button
                onClick={() => handleSpeak(isPaused ? 'play' : isSpeaking ? 'pause' : 'play')}
                className="tts-control-btn"
                title={isPaused ? 'Resume' : isSpeaking ? 'Pause' : 'Play'}
                aria-label={isPaused ? 'Resume speech' : isSpeaking ? 'Pause speech' : 'Play speech'}
              >
                {isPaused ? '▶️' : isSpeaking ? '⏸️' : '▶️'}
              </button>
              <button
                onClick={() => handleSpeak('stop')}
                className="tts-control-btn"
                title="Stop"
                aria-label="Stop speech"
              >
                ⏹️
              </button>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={speechRate}
                onChange={handleRateChange}
                className="tts-rate-slider"
                title="Adjust speech speed"
                aria-label="Adjust speech speed"
              />
              <span className="tts-rate-label">{speechRate.toFixed(1)}x</span>
            </div>
          )}
        </div>

        <div className="message-footer">
          <div className="message-info">
            <span className="message-timestamp">
              {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
            </span>
            {(editCount > 0 || regenerateCount > 0) && (
              <span className="version-info">
                {editCount > 0 && `Edited ${editCount} time${editCount > 1 ? 's' : ''}`}
                {editCount > 0 && regenerateCount > 0 && ' | '}
                {regenerateCount > 0 && `Version ${currentVersion}/${totalVersions}`}
              </span>
            )}
          </div>

          <div className="message-actions">
            {message.role === 'user' && (
              <>
                <button
                  onClick={handleEdit}
                  className="action-btn"
                  title="Edit message"
                  aria-label="Edit message"
                >
                  <span className="action-icon">✏️</span>
                </button>
                <button
                  onClick={handleCopy}
                  className={`action-btn ${copySuccess ? 'success' : ''}`}
                  title={copySuccess ? 'Copied!' : 'Copy message'}
                  aria-label="Copy message"
                >
                  <span className="action-icon">{copySuccess ? '✓' : '📋'}</span>
                </button>
                <button
                  onClick={handleShare}
                  className="action-btn"
                  title="Share message"
                  aria-label="Share message"
                >
                  <span className="action-icon">🔗</span>
                </button>
              </>
            )}

            {message.role === 'assistant' && (
              <>
                <button
                  onClick={handleCopy}
                  className={`action-btn ${copySuccess ? 'success' : ''}`}
                  title={copySuccess ? 'Copied!' : 'Copy message'}
                  aria-label="Copy message"
                >
                  <span className="action-icon">{copySuccess ? '✓' : '📋'}</span>
                </button>

                <button
                  onClick={handleLike}
                  className={`action-btn ${isLiked ? 'liked' : ''}`}
                  title="Like response"
                  aria-label="Like response"
                >
                  <span className="action-icon">👍</span>
                </button>

                <button
                  onClick={handleDislike}
                  className={`action-btn ${isDisliked ? 'disliked' : ''}`}
                  title="Dislike response"
                  aria-label="Dislike response"
                >
                  <span className="action-icon">👎</span>
                </button>

                <button
                  onClick={() => handleSpeak(isPaused ? 'play' : isSpeaking ? 'pause' : 'play')}
                  className={`action-btn tts-btn ${isSpeaking ? 'speaking' : ''}`}
                  title={isPaused ? 'Resume' : isSpeaking ? 'Pause' : 'Read aloud'}
                  aria-label={isPaused ? 'Resume speech' : isSpeaking ? 'Pause speech' : 'Read aloud'}
                >
                  <span className="action-icon">{isPaused ? '▶️' : isSpeaking ? '⏸️' : '🔊'}</span>
                </button>

                <button
                  onClick={handleShare}
                  className="action-btn"
                  title="Share response"
                  aria-label="Share response"
                >
                  <span className="action-icon">🔗</span>
                </button>

                <button
                  onClick={handleExportPDF}
                  className="action-btn"
                  title="Export to PDF"
                  aria-label="Export to PDF"
                >
                  <span className="action-icon">📄</span>
                </button>

                <button
                  onClick={handleTryAgain}
                  className="action-btn"
                  title="Try again"
                  aria-label="Regenerate response"
                >
                  <span className="action-icon">🔄</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;